import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Check, Clock, Users, Sparkles, ArrowRight } from 'lucide-react';
import { getServiceBySlug } from '../data/servicesData';
import { useBooking } from '../context/BookingContext';
import SEO from '../components/SEO';

const ServiceDetailPage = () => {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);
  const { openBooking } = useBooking();

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <SEO title="Service Not Found" path={`/services/${slug || ''}`} noindex />
        <div className="text-center px-6">
          <h1 className="text-4xl font-serif text-stone-900 mb-4">
            Service Not Found
          </h1>
          <p className="text-stone-600 mb-6">
            The service you’re looking for doesn’t exist or has moved.
          </p>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 font-medium"
            data-testid="service-not-found-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Services
          </Link>
        </div>
      </div>
    );
  }

  // Build schema for this specific service
  const procedureSchema = {
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: service.name,
    alternateName: service.tagline,
    description: service.fullDescription || service.shortDescription,
    image: service.image,
    url: `https://akirasignaturecare.com/services/${service.slug}`,
    procedureType: 'https://schema.org/NoninvasiveProcedure',
    preparation: service.howItWorks ? service.howItWorks.join('. ') : undefined,
    followup: service.results,
    performer: { '@id': 'https://akirasignaturecare.com/#clinic' },
  };
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://akirasignaturecare.com/' },
      { '@type': 'ListItem', position: 2, name: 'Services', item: 'https://akirasignaturecare.com/services' },
      { '@type': 'ListItem', position: 3, name: service.name, item: `https://akirasignaturecare.com/services/${service.slug}` },
    ],
  };

  // §5 — Service title upgrades: bespoke SEO titles + descriptions for the
  // highest-intent hub services. Everything else falls back to the generic
  // "{Service} in Alhambra, CA" pattern.
  const SEO_OVERRIDES = {
    'injectables-fillers': {
      title: 'Injectables & Fillers in Alhambra | Botox, Juvéderm | Akira',
      description:
        'Physician-led Botox®, Juvéderm® and Restylane® in Alhambra with Angela Trias, M.D. Consult-first, natural results. Call (626) 737-3183.',
    },
    'skin-microneedling': {
      title: 'Microneedling in Alhambra, CA | Akira Signature Care',
      description:
        'Microneedling in Alhambra — physician-led collagen induction with Angela Trias, M.D. for tone, texture and scarring. Call (626) 737-3183.',
    },
    'rf-microneedling': {
      title: 'RF Microneedling in Alhambra, CA | Akira Signature Care',
      description:
        'RF microneedling in Alhambra — physician-led collagen remodelling for firmer, smoother skin. Book with Angela Trias, M.D. (626) 737-3183.',
    },
    'xerf-structural-tightening': {
      title: 'XERF Skin Tightening in Alhambra | Akira Signature Care',
      description:
        'XERF structural skin tightening in Alhambra — physician-led non-surgical lifting with Angela Trias, M.D. Book (626) 737-3183.',
    },
    'prp-prf-regenerative-therapy': {
      title: 'PRP & PRF in Alhambra | Regenerative Therapy | Akira',
      description:
        'PRP & PRF regenerative therapy in Alhambra — physician-led collagen and skin renewal with Angela Trias, M.D. Book (626) 737-3183.',
    },
  };
  const override = SEO_OVERRIDES[service.slug];
  const seoTitle = override?.title || `${service.name} in Alhambra, CA`;
  const seoDescription = override?.description
    || (service.shortDescription || service.tagline || service.name).slice(0, 160);

  return (
    <div className="min-h-screen bg-stone-50" data-testid={`service-detail-${service.slug}`}>
      <SEO
        title={seoTitle}
        description={seoDescription}
        path={`/services/${service.slug}`}
        image={service.image}
        schema={[procedureSchema, breadcrumbSchema]}
      />
      {/* Hero Section with Image */}
      <section className="relative h-[60vh] min-h-[500px]">
        <img
          src={service.image}
          alt={service.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-transparent" />

        <div className="absolute inset-0 flex items-end">
          <div className="container mx-auto px-6 pb-16">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors w-fit"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Services
            </Link>

            {service.badge && (
              <div className="bg-white/90 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-semibold text-stone-800 mb-4 w-fit">
                {service.badge}
              </div>
            )}

            <h1
              className="font-serif text-5xl lg:text-6xl text-white mb-4"
              style={{ textShadow: '0 2px 24px rgba(0,0,0,0.45)' }}
            >
              {service.name}
            </h1>
            <p
              className="text-2xl text-white/90 max-w-3xl"
              style={{ textShadow: '0 1px 12px rgba(0,0,0,0.55)' }}
            >
              {service.tagline}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Left Column - Details */}
            <div className="lg:col-span-2 space-y-12">
              {/* Overview */}
              <div>
                <h2 className="font-serif text-3xl text-stone-900 mb-6">
                  Overview
                </h2>
                <p className="text-lg text-stone-700 leading-relaxed">
                  {service.fullDescription}
                </p>
              </div>

              {/* Local Alhambra landers callout — surfaces the city-specific
                  Botox / Fillers landing pages from the Injectables hub. */}
              {service.slug === 'injectables-fillers' && (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="injectables-local-landers">
                  <Link
                    to="/services/botox-alhambra"
                    data-testid="hub-link-botox-alhambra"
                    className="group block border border-rose-200 hover:border-rose-500 bg-rose-50/40 hover:bg-white p-6 transition-colors"
                  >
                    <div className="text-[10px] tracking-[0.3em] uppercase text-rose-500 mb-2">Alhambra</div>
                    <div className="font-serif text-xl text-stone-900 group-hover:text-rose-500 transition-colors mb-1">
                      Botox in Alhambra
                    </div>
                    <div className="text-sm text-stone-600">Physician-led, natural-looking neuromodulator treatment.</div>
                  </Link>
                  <Link
                    to="/services/dermal-fillers-alhambra"
                    data-testid="hub-link-fillers-alhambra"
                    className="group block border border-rose-200 hover:border-rose-500 bg-rose-50/40 hover:bg-white p-6 transition-colors"
                  >
                    <div className="text-[10px] tracking-[0.3em] uppercase text-rose-500 mb-2">Alhambra</div>
                    <div className="font-serif text-xl text-stone-900 group-hover:text-rose-500 transition-colors mb-1">
                      Dermal Fillers in Alhambra
                    </div>
                    <div className="text-sm text-stone-600">Juvéderm®, Restylane® &amp; biostimulators, planned by anatomy.</div>
                  </Link>
                </div>
              )}

              {/* How It Works */}
              {service.howItWorks && (
                <div>
                  <h2 className="font-serif text-3xl text-stone-900 mb-6">
                    How It Works
                  </h2>
                  <div className="space-y-4">
                    {service.howItWorks.map((step, index) => (
                      <div key={index} className="flex gap-4">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-100 text-rose-500 flex items-center justify-center font-semibold text-sm">
                          {index + 1}
                        </div>
                        <p className="text-stone-700 pt-1">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Treatments (for services with multiple options) */}
              {service.treatments && (
                <div>
                  <h2 className="font-serif text-3xl text-stone-900 mb-6">
                    Treatments Available
                  </h2>
                  <div className="grid sm:grid-cols-2 gap-4">
                    {service.treatments.map((treatment, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm"
                      >
                        <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                        <span className="text-stone-700">{treatment}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Includes (for precision health packages) */}
              {service.includes && (
                <div>
                  <h2 className="font-serif text-3xl text-stone-900 mb-6">
                    What’s Included
                  </h2>
                  <div className="space-y-3">
                    {service.includes.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-start gap-3 p-4 bg-white rounded-xl shadow-sm"
                      >
                        <Check className="w-5 h-5 text-rose-500 flex-shrink-0 mt-0.5" />
                        <span className="text-stone-700">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Benefits */}
              <div>
                <h2 className="font-serif text-3xl text-stone-900 mb-6">
                  Key Benefits
                </h2>
                <div className="grid sm:grid-cols-2 gap-4">
                  {service.benefits.map((benefit, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-4 bg-rose-50 rounded-xl"
                    >
                      <Check className="w-5 h-5 text-rose-500 flex-shrink-0" />
                      <span className="text-stone-800 font-medium">
                        {benefit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Column - Booking Card */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 bg-white rounded-2xl shadow-lg p-8 space-y-6">
                <div>
                  <div className="inline-block bg-rose-50 text-rose-500 text-[10px] tracking-[0.3em] uppercase px-3 py-1 mb-3">
                    {service.stripeUrl ? 'Purchase' : 'Personalised Consultation'}
                  </div>
                  <div className="font-serif text-2xl text-stone-900 mb-2 leading-snug">
                    Ready to start with {service.name}?
                  </div>
                  {service.price && (
                    <div
                      className="font-serif text-4xl text-stone-900 mt-4 mb-1"
                      data-testid={`service-detail-price-${service.slug}`}
                    >
                      {service.price}
                    </div>
                  )}
                  <p className="text-stone-500 text-sm">
                    {service.stripeUrl
                      ? 'Secure checkout via Stripe. We\u2019ll contact you within one business day to arrange testing and consultation.'
                      : 'Book a consultation and we\u2019ll design a plan around your goals.'}
                  </p>
                </div>

                {service.stripeUrl ? (
                  <>
                    <a
                      href={service.stripeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid={`service-detail-purchase-${service.slug}`}
                      className="w-full bg-rose-400 hover:bg-rose-500 text-white py-4 rounded-full font-semibold text-lg transition-colors flex items-center justify-center gap-2"
                    >
                      {service.ctaLabel || 'Purchase Now'}
                      <ArrowRight className="w-4 h-4" />
                    </a>
                    <button
                      onClick={() => openBooking(service.name)}
                      data-testid={`service-detail-book-${service.slug}`}
                      className="w-full border border-stone-300 hover:border-rose-400 text-stone-700 hover:text-rose-500 py-3 rounded-full font-medium text-sm transition-colors"
                    >
                      Or Book a Consultation First
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => openBooking(service.name)}
                    data-testid={`service-detail-book-${service.slug}`}
                    className="w-full bg-rose-400 hover:bg-rose-500 text-white py-4 rounded-full font-semibold text-lg transition-colors flex items-center justify-center gap-2"
                  >
                    Book This Treatment
                    <ArrowRight className="w-4 h-4" />
                  </button>
                )}

                {service.externalReference && (
                  <a
                    href={service.externalReference.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`service-detail-external-${service.slug}`}
                    className="block text-center text-xs text-stone-500 hover:text-rose-500 underline underline-offset-4 transition-colors"
                  >
                    {service.externalReference.label} ↗
                  </a>
                )}

                <div className="space-y-4 pt-6 border-t">
                  {service.idealFor && (
                    <div className="flex items-start gap-3">
                      <Users className="w-5 h-5 text-rose-500 mt-0.5" />
                      <div>
                        <div className="font-semibold text-stone-900 text-sm">
                          Ideal For
                        </div>
                        <div className="text-stone-600 text-sm">
                          {service.idealFor}
                        </div>
                      </div>
                    </div>
                  )}

                  {service.sessions && (
                    <div className="flex items-start gap-3">
                      <Clock className="w-5 h-5 text-rose-500 mt-0.5" />
                      <div>
                        <div className="font-semibold text-stone-900 text-sm">
                          Sessions
                        </div>
                        <div className="text-stone-600 text-sm">
                          {service.sessions}
                        </div>
                      </div>
                    </div>
                  )}

                  {service.downtime && (
                    <div className="flex items-start gap-3">
                      <Sparkles className="w-5 h-5 text-rose-500 mt-0.5" />
                      <div>
                        <div className="font-semibold text-stone-900 text-sm">
                          Downtime
                        </div>
                        <div className="text-stone-600 text-sm">
                          {service.downtime}
                        </div>
                      </div>
                    </div>
                  )}

                  {service.results && (
                    <div className="flex items-start gap-3">
                      <Check className="w-5 h-5 text-rose-500 mt-0.5" />
                      <div>
                        <div className="font-semibold text-stone-900 text-sm">
                          Results
                        </div>
                        <div className="text-stone-600 text-sm">
                          {service.results}
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="pt-6 border-t text-center">
                  <p className="text-stone-600 text-sm mb-3">
                    Questions about this treatment?
                  </p>
                  <Link
                    to="/contact"
                    className="text-rose-500 hover:text-rose-600 font-medium text-sm"
                  >
                    Contact Our Team →
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="bg-white py-16">
        <div className="container mx-auto px-6 text-center">
          <h2 className="font-serif text-3xl text-stone-900 mb-6">
            Explore More Treatments
          </h2>
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-rose-500 hover:text-rose-600 font-medium"
          >
            View All Services
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetailPage;
